import { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Link from '@mui/material/Link'
import TablePagination from '@mui/material/TablePagination'
import Typography from '@mui/material/Typography'

import { useCampaignListInstrumentMetrics } from '@/features/campaigns/api/useCampaignListInstrumentMetrics'
import {
  enrichCampaignsWithInstrumentMetrics,
} from '@/features/campaigns/lib/campaign-list-metrics'
import {
  matchesCampaignLandingSearch,
  matchesLandingFilter,
  sortCampaignsByName,
  sortCampaignsByUpdatedDesc,
  type CampaignLandingFilter,
} from '@/features/campaigns/lib/campaign-landing-display'
import type { Campaign } from '@/features/campaigns/types'
import { palettePrimaryMain } from '@/theme/designTokens'

import { ADMIN_DASHBOARD_LAYOUT } from '../lib/admin-dashboard-layout'
import { RECENT_CAMPAIGNS_TABLE } from '../lib/recent-campaigns-table-theme'
import { RecentCampaignsTable } from './RecentCampaignsTable'
import { RecentCampaignsToolbar } from './RecentCampaignsToolbar'

const DEFAULT_ROWS_PER_PAGE = 5
const ROWS_PER_PAGE_OPTIONS = [3, 5, 10, 15, 20] as const

const paginationSx = {
  '& .MuiTablePagination-toolbar': {
    minHeight: ADMIN_DASHBOARD_LAYOUT.paginationMinHeight,
    px: 0,
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: 14,
    color: '#4a5565',
    letterSpacing: '-0.1504px',
  },
  '& .MuiTablePagination-select': {
    bgcolor: ADMIN_DASHBOARD_LAYOUT.paginationSelectBg,
    borderRadius: '4px',
  },
  '& .MuiTablePagination-actions .MuiIconButton-root': {
    color: '#4a5565',
  },
} as const

interface Props {
  campaigns: Campaign[]
}

export function RecentCampaignsSection({ campaigns }: Readonly<Props>) {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<CampaignLandingFilter>('all')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE)
  const [nameSortDirection, setNameSortDirection] = useState<'asc' | 'desc' | null>(null)

  const allCampaignIds = useMemo(() => campaigns.map((c) => c.id), [campaigns])
  const { metricsByCampaignId } = useCampaignListInstrumentMetrics(allCampaignIds)

  const enrichedCampaigns = useMemo(
    () => enrichCampaignsWithInstrumentMetrics(campaigns, metricsByCampaignId),
    [campaigns, metricsByCampaignId],
  )

  const filteredItems = useMemo(() => {
    let rows = enrichedCampaigns.filter(
      (c) => matchesLandingFilter(c, statusFilter) && matchesCampaignLandingSearch(c, search),
    )
    if (nameSortDirection) {
      rows = sortCampaignsByName(rows, nameSortDirection)
    } else {
      rows = sortCampaignsByUpdatedDesc(rows)
    }
    return rows
  }, [enrichedCampaigns, statusFilter, search, nameSortDirection])

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage
    return filteredItems.slice(start, start + rowsPerPage)
  }, [filteredItems, page, rowsPerPage])

  const handleToggleNameSort = () => {
    setNameSortDirection((prev) => {
      if (prev === null) return 'asc'
      if (prev === 'asc') return 'desc'
      return null
    })
    setPage(0)
  }

  return (
    <Box data-testid="sensemaker-ftux-recent-campaigns">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: ADMIN_DASHBOARD_LAYOUT.recentSectionHeaderGap,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                lineHeight: '28px',
                color: ADMIN_DASHBOARD_LAYOUT.recentTitleColor,
                letterSpacing: ADMIN_DASHBOARD_LAYOUT.recentTitleLetterSpacing,
              }}
            >
              Recent Campaigns
            </Typography>
            <Link
              component={RouterLink}
              to="/campaigns"
              underline="hover"
              data-testid="admin-dashboard-campaigns-view-all"
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: palettePrimaryMain,
                whiteSpace: 'nowrap',
              }}
            >
              View all{campaigns.length > 0 ? ` (${campaigns.length})` : ''}
            </Link>
          </Box>
          <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.25 }}>
            Campaigns with Lines of Inquiry — continue building or open the launching screen
          </Typography>
        </Box>

        <RecentCampaignsToolbar
          search={search}
          onSearchChange={(value) => {
            setSearch(value)
            setPage(0)
          }}
          statusFilter={statusFilter}
          onStatusFilterChange={(value) => {
            setStatusFilter(value)
            setPage(0)
          }}
        />
      </Box>

      <Card
        elevation={0}
        sx={{
          bgcolor: RECENT_CAMPAIGNS_TABLE.bodyBg,
          border: `1px solid ${RECENT_CAMPAIGNS_TABLE.cardBorder}`,
          borderRadius: RECENT_CAMPAIGNS_TABLE.cardRadius,
          overflow: 'hidden',
        }}
      >
        {filteredItems.length === 0 ? (
          <Box
            sx={{ py: 6, textAlign: 'center', px: 3 }}
            data-testid="admin-dashboard-campaigns-empty"
          >
            <Typography sx={{ fontSize: 14, color: '#6b7280' }}>
              No campaigns yet. Create your first campaign to get started.
            </Typography>
          </Box>
        ) : (
          <RecentCampaignsTable
            campaigns={paginatedItems}
            nameSortDirection={nameSortDirection}
            onToggleNameSort={handleToggleNameSort}
          />
        )}
      </Card>

      {filteredItems.length > 0 && (
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          onPageChange={(_e, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[...ROWS_PER_PAGE_OPTIONS]}
          data-testid="admin-dashboard-campaigns-pagination"
          sx={paginationSx}
        />
      )}
    </Box>
  )
}
